// Regular expression that all names in the model must adhere to.
// - The name may not start with underscore, we reserve it for internal names.
// - The name may not contain '.', to allow us to construct paths.
export const NAME_PATTERN = '^[A-Za-z0-9][A-Za-z0-9_]*$';

// Reserved name to indicate functional unit node in an eval graph.
// Guaranteed to have no conflict since we reserve '__' prefix for internal use.
export const FUNCTIONAL_UNIT_NODE_NAME = '__functional_unit__';

// Yield kinds for model outputs
export const RESULT_KIND_KWH = 'kwh';
export const RESULT_KIND_KG_OF_REFRIGERANT = 'kg_of_refrigerant';
export const RESULT_KIND_MMBTU_OF_ENERGY = 'mmbtu_of_energy';
export const RESULT_KIND_LAND_AREA = 'land_area';
export const RESULT_KIND_EXPENDITURE_IN_USD = 'expenditure_in_usd';
export const RESULT_KIND_LITERS_OF_WATER_USED = 'liters_of_water_used';
export const RESULT_KIND_LITERS_OF_WATER_CONSUMED = 'liters_of_water_consumed';
export const RESULT_KIND_LITERS_OF_WATER_RECYCLED = 'liters_of_water_recycled';
export const RESULT_KIND_LITERS_OF_WATER_STORED = 'liters_of_water_stored';
export const RESULT_KIND_LITERS_OF_WATER_DISCHARGED =
  'liters_of_water_discharged';
export const RESULT_KIND_KG_OF_WASTE = 'kg_of_waste';
export const RESULT_KIND_KG_OF_POLLUTANT = 'kg_of_pollutant';
export const RESULT_KIND_KG_OF_MICROPLASTICS = 'kg_of_microplastics';
export const RESULT_KIND_KG_OF_SUBSTANCE_INFLOW = 'kg_of_substance_inflow';
export const RESULT_KIND_KG_OF_SUBSTANCE_OUTFLOW = 'kg_of_substance_outflow';
export const RESULT_KIND_KG_OF_MICROPLASTICS_INFLOW =
  'kg_of_microplastics_inflow';
export const RESULT_KIND_KG_OF_MICROPLASTICS_OUTFLOW =
  'kg_of_microplastics_outflow';
export const RESULT_KIND_GDP_IN_PPP_ADJUSTED_USD = 'usd_of_ppp_adjusted_gdp';
export const RESULT_KIND_PCAF_DATA_QUALITY_SCORE = 'pcaf_data_quality_score';

export const REQUIRED_ERROR_PHRASE = 'cannot be empty';

export const MAX_LENGTH_OF_NAMES = 75;

export const NAME_TOO_LONG_ERROR = ` is too long of a name: names can be no longer than ${MAX_LENGTH_OF_NAMES} characters.`;

/**
 * These are the "Watershed-approved" tags that should be used by default when
 * running any footprint. They are a subset of the `VerifiedCalculationTag`s. Add
 * to `VerifiedCalculationTag` when wanting to make a tag visually "official",
 * but add to this list if you're trying to make a tag actually run on every
 * footprint by default. Add to `DefaultCalculationTags` below if adding a new tag here!!
 */
type DefaultCalculationTag =
  | typeof DEFAULT_CALCULATION_TAG
  | typeof USEEIO_CALCULATION_TAG
  | typeof CEDA_CALCULATION_TAG;

export const DEFAULT_CALCULATION_TAG = 'ws_corporate_footprint';
export const USEEIO_CALCULATION_TAG = 'ws_corporate_footprint_useeio';
export const CEDA_CALCULATION_TAG = 'ws_corporate_footprint_ceda';
// intentionally NOT included in `DefaultCalculationTags` as we
// only include these for orgs with `FootprintProductLevelEmissions`
// enabled
export const FOOTPRINT_GEN_CONSUMER_GOODS_TAG = 'ws_consumer_goods_idi';

export type DefaultFinancialsCalculationTag =
  | typeof USEEIO_CALCULATION_TAG
  | typeof CEDA_CALCULATION_TAG;

export const DefaultCalculationTags: ReadonlyArray<DefaultCalculationTag> = [
  DEFAULT_CALCULATION_TAG,
  CEDA_CALCULATION_TAG,
  USEEIO_CALCULATION_TAG,
] as const;

/**
 * A type guard to check if a given string is really a DefaultCalculationTag.
 */
export const isDefaultCalculationTag = (
  tag: string
): tag is DefaultCalculationTag => {
  return DefaultCalculationTags.includes(tag as DefaultCalculationTag);
};

/**
 * These are "Watershed-approved" tags that should visually show a different
 * experience in the UI, and should be a superset of the `DefaultCalculationTag`s.
 */
type VerifiedCalculationTag =
  | DefaultCalculationTag
  | typeof CEE_CALCULATION_TAG
  | typeof FOOTPRINT_GEN_CONSUMER_GOODS_TAG;

export const CEE_CALCULATION_TAG = 'cee';

export const VerifiedCalculationTags: ReadonlyArray<VerifiedCalculationTag> = [
  ...DefaultCalculationTags,
  CEE_CALCULATION_TAG,
  FOOTPRINT_GEN_CONSUMER_GOODS_TAG,
] as const;

/**
 * A type guard to check if a given string is really a VerifiedCalculationTag.
 */
export const isVerifiedCalculationTag = (
  tag: string
): tag is VerifiedCalculationTag => {
  return VerifiedCalculationTags.includes(tag as VerifiedCalculationTag);
};

export const LSCRIPT_NAME_PREFIX_EMISSIONS_FACTOR = 'emissions_factor';
export const LSCRIPT_NAME_PREFIX_EMISSIONS_ACTIVITY = 'activity';

/**
 * Prefix added to all reference data, emissions model data types - in temporal workflows,
 * always use `autogeneratedUniqueResetDemoDataName` to access something with this
 * prefix applied already.
 */
export const AUTOGENERATED_PREFIX = 'autogen_rdd';

/**
 * Control flow helper for whether a tag is a consumer goods tag
 */
export function isConsumerGoodsTag(tag: string): boolean {
  return tag.startsWith('ws_consumer_goods');
}

/**
 * Slack ID for @calculation-oncall
 */
export const CALC_ONCALL_MENTION = `<!subteam^S04MTMG9R6V>`;

/***
 * Slack ID for @eng-oncall
 */
export const ENG_ONCALL_MENTION = `<!subteam^S05N8H2KBQQ>`;

/**
 * LEGACY! DO NOT USE UNLESS YOU KNOW THE TERRIBLE REPERCUSSIONS!
 *
 * Special local variable names that ReliableInputQuantityService looks for.
 *
 * These are not codegenned, just very unique so as not to conflict with other
 * variable names.
 */
const RIQS_LOCAL_VARIABLE_KEY_PRODUCT = 'wcg_product_quantity_6d4800da';
const RIQS_LOCAL_VARIABLE_KEY_MATERIAL = 'wcg_material_quantity_e45e216c';
const RIQS_LOCAL_VARIABLE_KEY_ELECTRICITY = 'wcg_electricity_quantity_k24e759j';
export const RIQS_LOCAL_VARIABLES = [
  RIQS_LOCAL_VARIABLE_KEY_PRODUCT,
  RIQS_LOCAL_VARIABLE_KEY_MATERIAL,
  RIQS_LOCAL_VARIABLE_KEY_ELECTRICITY,
];

/**
 * LEGACY! DO NOT USE UNLESS YOU KNOW THE TERRIBLE REPERCUSSIONS!
 *
 * Special region attribute names that ReliableInputQuantityService looks for.
 */
export const RIQS_REGION_ATTRIBUTES = [
  'org_material',
  'org_raw_material',
  'ws_raw_material',
  'product',
] as const;
